import { useEffect } from 'react'

import type { VideoData } from '@b-stock/fe-core/bstock-next/helpers'

import type { MediaType } from '@/buyer-portal/components/features/listing/media/shared/types'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

// Actions that are tracked and sent to Segment analytics
const trackableVideoAction = ['play', 'pause', 'ended'] as const
type TrackableVideoAction = (typeof trackableVideoAction)[number]

export interface UseVideoTrackingProps {
  videoRefsMap: React.RefObject<ReadonlyMap<number, HTMLIFrameElement>>
  media: MediaType[]
  analyticsData: Record<string, unknown>
}

interface VideoPlayerState {
  lastPlayerState?: number
  duration: number | null
  currentTime: number | null
}

type YoutubeMessageData = {
  event?: string
  func?: string
  data?: number
  info?: {
    playerState?: number
    currentTime?: number
    duration?: number
  }
  channel?: string
  args?: number[]
}

const vimeoSubscribedEvents = ['play', 'pause', 'ended', 'seeking'] as const
type VimeoMessageData = {
  event: (typeof vimeoSubscribedEvents)[number] | 'ready'
  data?: {
    seconds?: number
    duration?: number
  }
}

interface VideoTrackingContext {
  trackButtonClicked: (payload: unknown) => void
  analyticsData: UseVideoTrackingProps['analyticsData']
  mediaLength: number
}

// WeakMap to store video states by iframe reference
const videoStates = new WeakMap<HTMLIFrameElement, VideoPlayerState>()

// Maps YouTube playerState numbers to trackable actions
const youtubePlayerStateMap: Record<number, TrackableVideoAction> = {
  0: 'ended',
  1: 'play',
  2: 'pause',
}

/**
 * Validates if an action is trackable
 */
const isTrackableAction = (
  action?: string | null
): action is TrackableVideoAction => {
  return trackableVideoAction.includes(action as TrackableVideoAction)
}

/**
 * Sends a video tracking event to analytics (only for trackable actions)
 */
function trackVideoEvent(
  context: VideoTrackingContext,
  action: TrackableVideoAction,
  state: VideoPlayerState,
  index: number
) {
  context.trackButtonClicked({
    ...context.analyticsData,
    buttonName: 'video_click',
    video_action: action,
    video_length: state.duration,
    video_watch_progress: state.currentTime,
    media_index: index + 1,
    media_type: 'video',
    number_of_media_count: context.mediaLength,
  })
}

/**
 * Handles messages from the YouTube iframe API
 */
function handleYoutubeMessage(
  data: YoutubeMessageData,
  state: VideoPlayerState,
  currentSlide: number,
  context: VideoTrackingContext
) {
  const { info, channel } = data

  if (channel !== 'widget') {
    return
  }

  // always keep fresh data
  if (typeof info?.duration === 'number') {
    state.duration = Math.floor(info.duration)
  }

  if (typeof info?.currentTime === 'number') {
    state.currentTime = Math.floor(info.currentTime)
  }

  if (typeof info?.playerState !== 'number') {
    return
  }

  // Send tracking event only if playerState changed and is trackable
  const action = youtubePlayerStateMap[info.playerState]

  if (
    !isTrackableAction(action) ||
    state.lastPlayerState === info.playerState
  ) {
    return
  }

  // youtube sometimes has discrepancy in currentTime and duration in one second
  // set current time equal to duration if action is "ended"
  if (action === 'ended') {
    state.currentTime = state.duration
  }

  trackVideoEvent(context, action, state, currentSlide)

  state.lastPlayerState = info.playerState
}

/**
 * Handles messages from the Vimeo iframe API
 */
function handleVimeoMessage(
  data: VimeoMessageData,
  state: VideoPlayerState,
  currentSlide: number,
  context: VideoTrackingContext,
  videoIframe: HTMLIFrameElement
) {
  if (data.event === 'ready') {
    subscribeToEvents(videoIframe, 'vimeo')
    return
  }

  // Progress tracking - update state for all trackable and non-trackable events
  if (data.data?.seconds != null) {
    state.currentTime = Math.floor(data.data.seconds)
  }

  if (data.data?.duration != null) {
    state.duration = Math.floor(data.data.duration)
  }

  // Only track if event is trackable (play/pause/ended, not seeking)
  if (!isTrackableAction(data.event)) {
    return
  }

  // Vimeo sends pause and ended events when video ended, ignore pause in that case
  if (data.event === 'pause' && state.duration === state.currentTime) {
    return
  }

  trackVideoEvent(context, data.event, state, currentSlide)
}

/**
 * Subscribes to YouTube or Vimeo iframe events
 */
function subscribeToEvents(
  videoIframe: HTMLIFrameElement,
  platform: VideoData['platform']
) {
  if (platform === 'youtube') {
    videoIframe.contentWindow?.postMessage(
      JSON.stringify({ event: 'listening' }),
      'https://www.youtube.com'
    )
  }

  if (platform === 'vimeo') {
    // Subscribe to trackable events (play/pause/ended/seeking)
    for (const event of vimeoSubscribedEvents) {
      videoIframe.contentWindow?.postMessage(
        { method: 'addEventListener', value: event },
        'https://player.vimeo.com'
      )
    }
  }
}

// Outside useEffect
function handlePostMessage(
  event: MessageEvent,
  videoRefsMap: React.RefObject<ReadonlyMap<number, HTMLIFrameElement>>,
  media: UseVideoTrackingProps['media'],
  context: VideoTrackingContext
) {
  // Validate origin
  if (!/youtube\.com|youtube-nocookie\.com|vimeo\.com/.test(event.origin)) {
    return
  }

  // Find which iframe this message belongs to
  videoRefsMap.current.forEach((videoIframe, index) => {
    if (event.source !== videoIframe.contentWindow) {
      return
    }

    const state = videoStates.get(videoIframe)
    if (!state) {
      return
    }

    const mediaItem = media[index]
    const platform =
      mediaItem?.type === 'video' ? mediaItem.videoData.platform : undefined
    if (!platform) {
      return
    }

    let data: YoutubeMessageData | VimeoMessageData
    try {
      data = (
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data
      ) as YoutubeMessageData | VimeoMessageData
    } catch {
      return
    }

    if (platform === 'youtube') {
      handleYoutubeMessage(data as YoutubeMessageData, state, index, context)
    }

    if (platform === 'vimeo') {
      handleVimeoMessage(
        data as VimeoMessageData,
        state,
        index,
        context,
        videoIframe
      )
    }
  })
}

/**
 * Hook to track video interactions on a carousel or modal view
 */
export const useVideoTracking = ({
  videoRefsMap,
  media,
  analyticsData,
}: UseVideoTrackingProps) => {
  const { trackButtonClicked } = useAnalytics()

  // listens post events
  useEffect(() => {
    const context: VideoTrackingContext = {
      trackButtonClicked: trackButtonClicked as (payload: unknown) => void,
      analyticsData,
      mediaLength: media.length,
    }

    const listener = (event: MessageEvent) =>
      handlePostMessage(event, videoRefsMap, media, context)

    window.addEventListener('message', listener)
    return () => window.removeEventListener('message', listener)
  }, [videoRefsMap, media, analyticsData, trackButtonClicked])

  // subscribe to all iframe events
  useEffect(() => {
    // Subscribe all current youtube iframes whenever videoRefsMap changes
    // Vimeo iframes will be subscribed after the "ready" event received
    videoRefsMap.current.forEach((videoIframe, index) => {
      // Initialize state
      if (!videoStates.has(videoIframe)) {
        videoStates.set(videoIframe, { duration: null, currentTime: null })
      }

      const mediaItem = media[index]
      const platform =
        mediaItem?.type === 'video' ? mediaItem.videoData.platform : undefined
      if (platform === 'youtube') {
        subscribeToEvents(videoIframe, platform)

        // Fallback: subscribe again on load in case iframe is not yet loaded
        videoIframe.addEventListener(
          'load',
          () => subscribeToEvents(videoIframe, platform),
          { once: true }
        )
      }
    })
  }, [videoRefsMap, media])
}
